'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type Fuse from 'fuse.js';
import type { FuseResult, IFuseOptions } from 'fuse.js';
import { searchIndexData } from '@/data/searchIndex.data';
import { SearchItem } from '@/types/search.types';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const MAX_RESULTS = 8;

const FUSE_OPTIONS: IFuseOptions<SearchItem> = {
  includeScore: true,
  includeMatches: true,
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 2,
  keys: [
    { name: 'title', weight: 0.5 },
    { name: 'keywords', weight: 0.3 },
    { name: 'category', weight: 0.1 },
    { name: 'description', weight: 0.1 },
  ],
};

export type SiteSearchResult = FuseResult<SearchItem>;

type UseSiteSearchReturn = {
  query: string;
  debouncedQuery: string;
  setQuery: (value: string) => void;
  results: SiteSearchResult[];
  isReady: boolean;
  hasQuery: boolean;
  hasResults: boolean;
  reset: () => void;
};

export const useSiteSearch = (): UseSiteSearchReturn => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [fuse, setFuse] = useState<Fuse<SearchItem> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let isMounted = true;

    import('fuse.js').then((module) => {
      if (!isMounted) return;
      const FuseConstructor = module.default;
      setFuse(new FuseConstructor(searchIndexData, FUSE_OPTIONS));
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query]);

  const results = useMemo<SiteSearchResult[]>(() => {
    const trimmed = debouncedQuery.trim();

    if (!fuse || trimmed.length < MIN_QUERY_LENGTH) {
      return [];
    }

    return fuse.search(trimmed, { limit: MAX_RESULTS });
  }, [fuse, debouncedQuery]);

  const reset = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  return {
    query,
    debouncedQuery,
    setQuery,
    results,
    isReady: fuse !== null,
    hasQuery: query.trim().length >= MIN_QUERY_LENGTH,
    hasResults: results.length > 0,
    reset,
  };
};
